import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { FormAdapter, FormAlert, FormContainerOptions, FormControlProperties, FormControlType, FormValues } from '@omegagrid/form';
import { NodeSource } from "@omegagrid/tree";
import rp, { BrowserAdapter, fromIsoDate, toIsoDate } from '@reporting/rp-app-host';
import { TicketBrowserAdapter } from "../adapters";
import { FilterOptions, HelpdeskLists, REQUEST_PRIORITIES, REQUEST_TYPES, REQUEST_STATUSES } from "../types";
import { Accounting, AccountingRights, User } from '@reporting/rp-types/core';

export class FilterFormAdapter extends FormAdapter {

	constructor(
		private browserAdapter: TicketBrowserAdapter,
	) { super() }

	private formFields = ['priority', 'id_type', 'status', 'accounting', 'who_responsible', 'escalation', 'is_open'] as const;
	private _lists: HelpdeskLists = {
		id_type: REQUEST_TYPES.map(item => ({ k: item.k, v: msg(item.v) })),
		status: REQUEST_STATUSES.map(item => ({ k: item.k, v: msg(item.v) })),
		priority: REQUEST_PRIORITIES.map(item => ({ k: item.k, v: msg(item.v) })),
		who_responsible: [],
	};

	getColumns() {
		return [
			{ width: 100 },
			{ width: null },
		];
	}

	/**
	 * Groups with their companies; only a company can be picked (a group node has no checkbox, a click on it
	 * expands it). The current group and the groups with a selected company are expanded.
	 */
	private createAccountingTree(accountings: Accounting[], selected: number[]): NodeSource[] {
		const currentGroup = rp.accountingProvider.accounting?.id_accounting_group;
		const selectedSet = new Set(selected ?? []);
		const groups = new Map<number, Accounting[]>();
		[...accountings]
			.sort((a, b) => Number(b.id_accounting_group === currentGroup) - Number(a.id_accounting_group === currentGroup)
				|| (a.group_name ?? '').localeCompare(b.group_name ?? '')
				|| a.accounting - b.accounting)
			.forEach(a => {
				if (!groups.has(a.id_accounting_group)) groups.set(a.id_accounting_group, []);
				groups.get(a.id_accounting_group).push(a);
			});
		const nodes: NodeSource[] = [];
		for (const [g, items] of groups) {
			nodes.push({
				k: `g-${g}`,
				v: `${g} ${items[0].group_name ?? ''}`.trim(),
				l: 0,
				ch: false,
				collapsed: g !== currentGroup && !items.some(a => selectedSet.has(a.accounting)),
				folder: BrowserAdapter.folderOptions,
			});
			// the key stays a number - the filter values (and the request where) are numbers
			items.forEach(a => nodes.push({ k: a.accounting as unknown as string, v: `${a.accounting} ${a.company}`, l: 1 }));
		}
		return nodes;
	}

	async getControls() {
		const activeFilter = await this.browserAdapter.getActiveFilter();
		const activeFilterMod: any = (() => {
			const values: any = {};
			this.formFields.forEach(field => {
				values[field] = activeFilter[field] ? activeFilter[field].map((v: any) => [v, 0, 1]) : null;
			});
			values.date_expected = fromIsoDate(activeFilter.date_expected);
			return values;
		})();
		// SA: all the groups (no group in the url); the others get their current group, narrowed to the helpdesk right
		const accountings = await rp.ms('accounting').get<Accounting[]>('accountings', {
			fields: ['accounting', 'company', 'id_accounting_group', 'group_name'],
			right: AccountingRights.Helpdesk,
		});
		const yes_no = [{k: '1', v: msg('Yes')},	{k: '0', v: msg('No')}];
		const controls: FormControlProperties[] = [
			{ row: 0, col: 0, type: FormControlType.Label, value: msg('Priority'), for: 'priority' },
			{ row: 0, col: 1, type: FormControlType.List, key: 'priority', value: activeFilterMod.priority, content: {
				sourceData: this._lists.priority, options: { selectMode: 'multiple', autoClose: false }
			}},
			{ row: 1, col: 0, type: FormControlType.Label, value: msg('Type'), for: 'id_type' },
			{ row: 1, col: 1, type: FormControlType.List, key: 'id_type', value: activeFilterMod.id_type, content: {
				sourceData: this._lists.id_type, options: { selectMode: 'multiple', autoClose: false }
			}},
			{ row: 2, col: 0, type: FormControlType.Label, value: msg('Status'), for: 'status' },
			{ row: 2, col: 1, type: FormControlType.List, key: 'status', value: activeFilterMod.status, content: {
				sourceData: this._lists.status, options: { selectMode: 'multiple', autoClose: false }
			}},
			{ row: 3, col: 0, type: FormControlType.Label, value: msg('Company'), for: 'accounting' },
			{ row: 3, col: 1, type: FormControlType.List, key: 'accounting', value: activeFilterMod.accounting, content: {
				sourceData: this.createAccountingTree(accountings ?? [], activeFilter.accounting),
				// no global level buttons: their "1" would select the groups (the root nodes) themselves
				options: { selectMode: 'multiple', autoClose: false, search: true, globalLevels: false }
			}},
			{ row: 4, col: 0, type: FormControlType.Label, value: msg('Date expected'), for: 'date_expected' },
			{ row: 4, col: 1, type: FormControlType.CalendarDateInput, key: 'date_expected', required: false, value: activeFilterMod.date_expected },
			{ row: 5, col: 0, type: FormControlType.Label, value: msg('Escalation'), for: 'escalation' },
			{ row: 5, col: 1, type: FormControlType.List, key: 'escalation', value: activeFilterMod.escalation, content: {
				sourceData: yes_no, options: { selectMode: 'multiple', autoClose: false }
			}},
			{ row: 6, col: 0, type: FormControlType.Label, value: msg('Is open'), for: 'is_open' },
			{ row: 6, col: 1, type: FormControlType.List, key: 'is_open', value: activeFilterMod.is_open, content: {
				sourceData: yes_no,	options: { selectMode: 'multiple', autoClose: false }
			}},
		];
		if (rp.userProvider.isSA()) {
			const saUsers = await rp.ms('users').get<User[]>('saUsers');
			this._lists.who_responsible = saUsers.map(u => ({ k: u.user_name, v: u.login_name }));
			controls.push(
				{ row: 7, col: 0, type: FormControlType.Label, value: msg('Responsible'), for: 'who_responsible' },
				{ row: 7, col: 1, type: FormControlType.List, key: 'who_responsible', value: activeFilterMod.who_responsible, content: {
					sourceData: this._lists.who_responsible, options: { selectMode: 'multiple', autoClose: true, search: true }
				}}
			);
		}
		return controls;
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: [{key: "save", text: msg("Apply"), color: ColorTypes.Accent}, "close"]
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		let filter: FilterOptions = {};
		this.formFields.forEach(field => {
			if (Array.isArray(formValues[field]) && (formValues[field] as any[]).length > 0) {
				filter[field] = (formValues[field] as any[][]).map((item: any[]) => item[0]);
			}
		});
		// only companies - a group key ("g-...") must not reach the request where
		if (filter.accounting) {
			filter.accounting = filter.accounting.filter(a => typeof a === 'number');
			if (!filter.accounting.length) delete filter.accounting;
		}
		// the calendar holds a Date (the one the form opened with, or the picked one): the filter keeps the day
		const dateExpected = toIsoDate(formValues.date_expected as Date | string);
		if (dateExpected) filter.date_expected = dateExpected;
		this.browserAdapter.activeFilter = filter
		return true;
	}
};
